import { IFormat } from '@kurtosys/types/data/index.js';
import { IExecutionOptions, IQueryOptions } from '@kurtosys/types/query/index.js';
import { ITransformPivotField } from '@kurtosys/types/query/transforms/ITransformPivotField.js';

import { commonUtils } from '../../common/index.js';
import { getQueryClass } from '../queryUtils/getQueryClass.js';

import { BaseTransform } from './BaseTransform.js';

export class PivotTransform extends BaseTransform {
	private keysToRemove: string[] = [];
	private fieldHasValue = false;

	execute(queryResponse: any, queryOptions: IQueryOptions, executionOptions: IExecutionOptions): any[] {
		let response: any[] = [];
		if (queryResponse) {
			if (commonUtils.isArrayLike(queryResponse) || typeof queryResponse === 'object') {
				let elements = queryResponse;
				if (!commonUtils.isArrayLike(queryResponse)) {
					elements = [queryResponse];
				}
				if (elements.length > 0) {
					if (this.options.pivotGroupByKey) {
						response = this.pivotGroupByElements(elements, executionOptions);
					} else {
						response.push(...this.pivotElement(elements, executionOptions));
					}
				}
			}
		}
		return response;
	}

	pivotElement(elements: object[], executionOptions: IExecutionOptions): object[] {
		const { fields } = this.options;
		const response: any[] = [];
		for (const field of fields) {
			const { key, name, isPrimary, format } = field as ITransformPivotField;
			let nameValue = key;
			if (name) {
				const nameQuery = getQueryClass(this.dependencies, name, this.translationHelper);
				if (nameQuery) {
					nameValue = nameQuery.execute(executionOptions);
				}
			}
			let record: object = {
				name: nameValue,
			};

			let index = 0;
			for (const element of elements) {
				this.fieldHasValue = false;
				const result = this.getRecord(element, key, isPrimary, format, executionOptions, ++index);
				record = Object.assign(record, result);
			}
			if (this.fieldHasValue) {
				response.push(record);
			}
		}

		return this.cleanUpKeys(response);
	}

	pivotGroupByElements(elements: object[], executionOptions: IExecutionOptions): object[] {
		const { fields, pivotGroupByKey } = this.options;
		// Array.from is a work around without having to set the downlevelIteration flag in ts config
		const pivotGroupByKeyValues: any[] = [
			...Array.from(new Set(elements.map((element) => element[pivotGroupByKey]))),
		];
		const response: object[] = [];

		for (const pivotGroupByKeyValue of pivotGroupByKeyValues) {
			for (const field of fields) {
				const { key, name, isPrimary, format } = field as ITransformPivotField;
				let record = {};
				let nameValue = key;

				record[pivotGroupByKey] = pivotGroupByKeyValue;

				if (name) {
					const nameQuery = getQueryClass(this.dependencies, name, this.translationHelper)
					if (nameQuery) {
						nameValue = nameQuery.execute(executionOptions);
					}
				}

				record['name'] = nameValue;

				// Only get elements for this group
				const groupedElements = elements.filter((element) => element[pivotGroupByKey] === pivotGroupByKeyValue);
				let index = 0;
				for (const groupedElement of groupedElements) {
					this.fieldHasValue = false;
					const result = this.getRecord(
						groupedElement,
						key,
						isPrimary,
						format,
						executionOptions,
						++index,
						pivotGroupByKeyValue,
					);
					record = Object.assign(record, result);
				}
				if (this.fieldHasValue) {
					response.push(record);
				}
			}
		}

		return this.cleanUpKeys(response, pivotGroupByKey);
	}

	getRecord(
		element: Record<string, any>,
		key: string,
		isPrimary: boolean,
		format: IFormat,
		executionOptions: IExecutionOptions,
		index: number,
		groupKey?: string,
	): object {
		const { pivotKey, pivotValueType, rawFieldKeys, pivotTransformOptions } = this.options;
		const record: object = {};

		if (rawFieldKeys) {
			for (const rawFieldKey of rawFieldKeys) {
				record[rawFieldKey] = commonUtils.get(element, rawFieldKey);
			}
		}
		let fieldKey = commonUtils.get(element, pivotKey);
		if (!this.keysToRemove.includes(fieldKey)) {
			let fieldValue = commonUtils.get(element, key);
			if (pivotValueType) {
				if (pivotValueType === 'number') {
					fieldValue = parseFloat(fieldValue);
				}
				if (pivotValueType === 'string') {
					fieldValue = fieldValue.toString();
				}
			}
			if (!commonUtils.isNullOrUndefined(fieldValue) && !Number.isNaN(fieldValue)) {
				if (format) {
					const { type, transforms, options } = format;
					const { culture, currency, translate } = executionOptions;
					fieldValue = commonUtils.spreadFormatter(
						type,
						culture,
						currency,
						options,
						fieldValue,
						transforms,
						translate,
					);
				}
				this.fieldHasValue = true;

				if (pivotTransformOptions?.incrementalKeyPrefix) {
					fieldKey = `${pivotTransformOptions.incrementalKeyPrefix}${index}`;
				}
				record[fieldKey] = fieldValue;
			} else if (isPrimary) {
				const keyToRemove = groupKey ? `${fieldKey}-${groupKey}` : fieldKey;
				this.keysToRemove.push(keyToRemove);
			}
		}
		return record;
	}

	cleanUpKeys(response: object[], pivotGroupByKey?: string): object[] {
		const { rawFieldKeys = [] } = this.options;
		const result: object[] = response;
		// This will track all fields that are undefined or null for the primary record
		// If the primary record doesn't have a value then we ignore the values for the
		// other records. Usually needed when a fund does not have a value but its benchmarks
		// but we don't want to show the benchmark values then.
		if (this.keysToRemove && this.keysToRemove.length > 0) {
			const staticKeys = ['name', ...rawFieldKeys];
			for (let i = 0; i < response.length; i++) {
				const record = response[i];
				for (const keyToRemove of this.keysToRemove) {
					if (pivotGroupByKey) {
						const keys = keyToRemove.split('-');
						if (record[pivotGroupByKey] === keys[1]) {
							delete record[keys[0]];
						}
					} else {
						delete record[keyToRemove];
					}
				}
				const remainingKeys = Object.keys(record);
				let hasValue = false;
				for (const remainingKey of remainingKeys) {
					hasValue = !staticKeys.includes(remainingKey);
					if (hasValue) {
						break;
					}
				}
				if (!hasValue) {
					response.splice(i, 1);
					i--;
				}
			}
		}
		return result;
	}
}
